import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Skeleton from "@mui/material/Skeleton";
import Typography from "@mui/material/Typography";
import Guid from "_shared/guids/guid";
import type { PermissionKind } from "permissions/_api/permission-kinds-api-service";
import RecordAccessPanel from "permissions/_views/records/record-access-panel";
import { recordKindOf, recordTitle } from "permissions/_views/records/record-caption";
import { type RecordDomain, type RecordPermissionsState, recordPermissionsPath } from "permissions/_views/records/record-permissions-path";
import useRecordGrants from "permissions/_views/records/use-record-grants";
import useRelatedRecords from "permissions/_views/records/use-related-records";
import { recordPage } from "permissions/_views/wording/permission-wording";
import { useState } from "react";
import { Link as RouterLink, useLocation } from "react-router";

/**
 * A proposal's or a work agreement's own conversations and documents, each a collapsible group under
 * the record's rows ("Conversation: Proposal discussion · 2 people"), with the same rows and controls
 * as its own record page and a link to that page for Check access (`docs/design/permissions/
 * PERMISSIONS_UI_DESIGN.md`, "The record page").
 */

/** Up to this many groups start open; past it, every group starts closed so the record's own rows stay in view. */
export const RELATED_GROUPS_OPEN_UP_TO = 3;

type Group = Readonly<{ domain: RecordDomain; kind: PermissionKind; recordId: string }>;

function RelatedRecordGroup(
  props: Readonly<{ group: Group; kinds: readonly PermissionKind[]; parentNoun: string; defaultExpanded: boolean }>,
) {
  const { group } = props;
  const location = useLocation();
  const noun = group.kind.targetNoun ?? "record";
  const recordId = new Guid(group.recordId);
  const [expanded, setExpanded] = useState(props.defaultExpanded);
  const [pageIndex, setPageIndex] = useState(0);
  const grants = useRecordGrants(group.kind, recordId, pageIndex);
  const title = recordPage.relatedRecord(noun, recordTitle(grants.target, noun), grants.holderCount);
  const summaryId = `related-${group.recordId}-summary`;
  // The record's own page sends the reader back here.
  const state: RecordPermissionsState = { from: { pathname: location.pathname, search: location.search, label: `the ${props.parentNoun}` } };

  return (
    <Accordion
      expanded={expanded}
      onChange={(_, open) => setExpanded(open)}
      disableGutters
      variant="outlined"
      slotProps={{ transition: { unmountOnExit: true } }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />} id={summaryId} aria-controls={`related-${group.recordId}-details`}>
        <Typography variant="subtitle2" component="h3" sx={{ overflowWrap: "anywhere" }}>
          {title}
        </Typography>
      </AccordionSummary>
      <AccordionDetails id={`related-${group.recordId}-details`} sx={{ display: "grid", gap: 1.5 }}>
        <Box>
          <Button component={RouterLink} to={recordPermissionsPath(group.domain, recordId)} state={state} size="small" startIcon={<OpenInFullIcon />}>
            {recordPage.openFullPage}
          </Button>
        </Box>
        <RecordAccessPanel
          kind={group.kind}
          kinds={props.kinds}
          recordId={recordId}
          recordName={grants.target.state === "found" ? grants.target.name : undefined}
          grants={grants}
          pageIndex={pageIndex}
          onPageChange={setPageIndex}
          idPrefix={`related-${group.recordId}`}
          hideCheckAccess
        />
      </AccordionDetails>
    </Accordion>
  );
}

export default function RelatedRecordGroups(
  props: Readonly<{ domain: RecordDomain; recordId: string; noun: string; kinds: readonly PermissionKind[] }>,
) {
  const related = useRelatedRecords(props.domain, props.recordId);
  const forumKind = recordKindOf(props.kinds, "forums");
  const documentKind = recordKindOf(props.kinds, "documents");
  const groups: Group[] = [
    ...(forumKind ? related.forumIds.map((recordId) => ({ domain: "forums" as const, kind: forumKind, recordId })) : []),
    ...(documentKind ? related.documentIds.map((recordId) => ({ domain: "documents" as const, kind: documentKind, recordId })) : []),
  ];

  let body;
  if (related.isError) {
    body = <Alert severity="warning">{recordPage.relatedRecordsFailed}</Alert>;
  } else if (related.isLoading) {
    body = <Skeleton variant="rectangular" height={48} aria-label="Loading related records" />;
  } else if (groups.length === 0) {
    return null;
  } else {
    const defaultExpanded = groups.length <= RELATED_GROUPS_OPEN_UP_TO;
    body = groups.map((group) => (
      <RelatedRecordGroup key={`${group.domain}-${group.recordId}`} group={group} kinds={props.kinds} parentNoun={props.noun} defaultExpanded={defaultExpanded} />
    ));
  }

  return (
    <Box component="section" aria-labelledby="related-records-title" sx={{ display: "grid", gap: 1 }}>
      <Typography variant="subtitle1" component="h2" id="related-records-title">
        {recordPage.relatedRecords(props.noun)}
      </Typography>
      {body}
    </Box>
  );
}
